import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import { IconButton, Tooltip } from '@mui/material'
import { useEffect, useState } from 'react'
import { DropAlert } from '../DropAlert'
import { DropPaper } from '../DropPaper'
import { AddAttributeDialog } from './AddAttributeDialog'
import { AddAttributeValueDialog } from './AddAttributeValueDialog'
import { AttributesTable } from './AttributesTable'
import { BlockAttributeDialog } from './BlockAttributeDialog'
import { DeleteAttributeDialog } from './DeleteAttributeDialog'
import { DisplayAllImagesDialog } from './DisplayAllImagesDialog'
import { PfpPreview } from './PfpPreview'
import MainButton from '../../common/util/input/MainButton'

export const AttributeEditor = ({
    attributes,
    setAttributes,
    blockRules,
    setBlockRules,
    hasInconsistentResolutions,
}) => {
    const [parseError, setParseError] = useState(null)
    const [selectedEditValue, setSelectedEditValue] = useState('')

    const [addAttributeDialogOpened, setAddAttributeDialogOpened] =
        useState(false)
    const [selectedAddValueAttribute, setSelectedAddValueAttribute] =
        useState(null)
    const [
        selectedBlockAttributesAttribute,
        setSelectedBlockAttributesAttribute,
    ] = useState(null)
    const [selectedDeleteAttribute, setSelectedDeleteAttribute] = useState(null)
    const [
        selectedDisplayAllImagesAttribute,
        setSelectedDisplayAllImagesAttribute,
    ] = useState(null)

    const handleSavedStateUpload = async (e) => {
        setParseError(null)
        const file = e.target.files[0]
        if (!file) {
            // User closed file picker
            return
        }
        const fileContents = await file.text()
        const parsed = JSON.parse(fileContents)
        if (!parsed || !parsed.attributes || !parsed.blockRules) {
            setParseError('File could not be read!')
        } else {
            setAttributes(parsed.attributes)
            setBlockRules(parsed.blockRules)
        }
    }

    const [displayedAttributeValue, setDisplayedAttributeValue] = useState(
        new Map(),
    )

    const getDisplayedAttributeValueIndex = (attributeName) => {
        if (displayedAttributeValue.has(attributeName)) {
            return displayedAttributeValue.get(attributeName)
        } else {
            return 0
        }
    }

    const isIllegalCombination = () => {
        const currentAttributeValueCombinations = []

        for (const attribute of attributes) {
            const attributeName = attribute.attribute_name
            const attributeValue =
                attribute.possible_values.length > 0
                    ? attribute.possible_values[
                          getDisplayedAttributeValueIndex(attributeName)
                      ]?.value
                    : null
            currentAttributeValueCombinations.push({
                name: attributeName,
                value: attributeValue,
            })
        }

        for (const comb of currentAttributeValueCombinations) {
            for (const rule of blockRules) {
                if (
                    comb.name !== rule.base_attribute.attribute_name ||
                    comb.value !== rule.base_attribute.value
                ) {
                    continue
                }
                for (const blackListItem of rule.attribute_blacklist) {
                    for (const comb2 of currentAttributeValueCombinations) {
                        if (
                            blackListItem.attribute_name === comb2.name &&
                            blackListItem.value === comb2.value
                        ) {
                            return (
                                <div>
                                    <b>
                                        {comb.name}: {comb.value}
                                    </b>{' '}
                                    is incompatible with{' '}
                                    <b>
                                        {comb2.name}: {comb2.value}
                                    </b>
                                    . This combination will not be minted.
                                </div>
                            )
                        }
                    }
                }
            }
        }

        return null
    }

    const updateDisplayedAttributeValueIndex = (attributeName, newIndex) => {
        const temp = new Map(displayedAttributeValue)
        temp.set(attributeName, newIndex)
        setDisplayedAttributeValue(temp)
    }

    useEffect(() => {
        const temp = new Map(displayedAttributeValue)
        let changed = false
        for (const attribute of attributes) {
            const attributeName = attribute.attribute_name
            let curIndex = 0

            if (displayedAttributeValue.has(attributeName)) {
                curIndex = displayedAttributeValue.get(attributeName)
            } else {
                changed = true
                temp.set(attributeName, 0)
                curIndex = 0
            }

            const maxIndex = getValuesAmountInAttribute(attributeName) - 1
            if (curIndex >= maxIndex) {
                temp.set(attributeName, maxIndex)
                changed = true
            }
        }
        if (changed) {
            setDisplayedAttributeValue(temp)
        }
    }, [attributes])

    const getValuesAmountInAttribute = (attributeName) => {
        const attribute = attributes.find(
            (element) => element.attribute_name === attributeName,
        )
        return attribute.possible_values.length
    }

    const getChance = (attributeName, attributeIndex) => {
        const attribute = attributes.find(
            (element) => element.attribute_name === attributeName,
        )
        const chance =
            attribute.possible_values.length > 0
                ? attribute.possible_values[attributeIndex]?.chance
                : null
        return chance
    }

    const setChance = (attributeName, attributeIndex, chance) => {
        let newChance = parseFloat(chance) >= 0 ? parseFloat(chance) : 0
        const temp = [...attributes]
        const targetAttribute = temp.find(
            (element) => element.attribute_name === attributeName,
        )
        targetAttribute.possible_values[attributeIndex].chance = newChance
        setAttributes(temp)
    }

    const getChancePercentage = (attributeName, attributeIndex) => {
        const attribute = attributes.find(
            (element) => element.attribute_name === attributeName,
        )
        let attributeChance
        let totalChance = 0
        for (var i = 0; i < attribute.possible_values.length; i++) {
            if (i === attributeIndex) {
                attributeChance = attribute.possible_values[i].chance
            }
            totalChance += attribute.possible_values[i].chance
        }

        return attributeChance > 0
            ? parseFloat(((attributeChance / totalChance) * 100).toFixed(2)) +
                  '%'
            : 'INVALID'
    }

    const updateLayers = (newLayer, index) => {
        const temp = [...attributes]

        for (const curValue of temp[index].possible_values) {
            curValue.layers[0].layer = newLayer
        }

        temp.sort((a, b) => {
            return (
                a.possible_values[0].layers[0].layer -
                b.possible_values[0].layers[0].layer
            )
        })
        setAttributes(temp)
    }

    const calculateRenderHashes = () => {
        const renderLayers = []
        for (const attribute of attributes) {
            const layers =
                attribute.possible_values[
                    getDisplayedAttributeValueIndex(attribute.attribute_name)
                ]?.layers
            if (layers) {
                for (const layer of layers) {
                    if (layer.ipfs !== '') {
                        renderLayers.push(layer)
                    }
                }
            }
        }
        renderLayers.sort((a, b) => {
            return a.layer - b.layer
        })
        return renderLayers
    }

    const isLayerDuplicated = (layer) => {
        const allLayers = []
        for (const attribute of attributes) {
            allLayers.push(attribute.possible_values[0].layers[0].layer)
        }
        const filtered = allLayers.filter((element) => element === layer)
        return filtered.length > 1
    }

    const hasDuplicateLayers = () => {
        const allLayers = []
        for (const attribute of attributes) {
            allLayers.push(attribute.possible_values[0].layers[0].layer)
        }
        return allLayers.length !== new Set(allLayers).size
    }

    return (
        <DropPaper
            id={'AttributeEditor'}
            title="Attributes"
            error={attributes.length === 0 || hasDuplicateLayers()}
        >
            <AddAttributeDialog
                attributes={attributes}
                setAttributes={setAttributes}
                addAttributeDialogOpened={addAttributeDialogOpened}
                setAddAttributeDialogOpened={setAddAttributeDialogOpened}
            />
            <DeleteAttributeDialog
                attributes={attributes}
                setAttributes={setAttributes}
                blockRules={blockRules}
                setBlockRules={setBlockRules}
                attributeToDelete={selectedDeleteAttribute}
                setAttributeToDelete={setSelectedDeleteAttribute}
            />
            <BlockAttributeDialog
                attributes={attributes}
                blockRules={blockRules}
                setBlockRules={setBlockRules}
                selectedBlockAttributesAttribute={
                    selectedBlockAttributesAttribute
                }
                setSelectedBlockAttributesAttribute={
                    setSelectedBlockAttributesAttribute
                }
            />
            <AddAttributeValueDialog
                attributes={attributes}
                setAttributes={setAttributes}
                blockRules={blockRules}
                setBlockRules={setBlockRules}
                selectedAddValueAttribute={selectedAddValueAttribute}
                setSelectedAddValueAttribute={setSelectedAddValueAttribute}
                selectedEditValue={selectedEditValue}
                setSelectedEditValue={setSelectedEditValue}
            />
            <DisplayAllImagesDialog
                attributes={attributes}
                selectedDisplayAllImagesAttribute={
                    selectedDisplayAllImagesAttribute
                }
                setSelectedDisplayAllImagesAttribute={
                    setSelectedDisplayAllImagesAttribute
                }
            />
            <DropAlert
                severity="warning"
                closable
                title="Important!"
                className="flex justify-center mb-5"
                condition={attributes.length === 0}
            >
                Please make sure that <b>all</b> of the images you are using in
                this drop have the <b>same resolution</b>. We recommend that
                they <b>don't exceed 2000 Pixels in width or height</b>, as
                images larger than that will take very long to render when
                minting final PFP NFTs.
            </DropAlert>
            <div className="flex flex-wrap justify-evenly">
                {attributes.length > 0 ? (
                    <PfpPreview
                        calculateRenderHashes={calculateRenderHashes}
                        isIllegalCombination={isIllegalCombination}
                    />
                ) : null}
                <AttributesTable
                    attributes={attributes}
                    isLayerDuplicated={isLayerDuplicated}
                    getDisplayedAttributeValueIndex={
                        getDisplayedAttributeValueIndex
                    }
                    updateDisplayedAttributeValueIndex={
                        updateDisplayedAttributeValueIndex
                    }
                    getValuesAmountInAttribute={getValuesAmountInAttribute}
                    getChance={getChance}
                    setChance={setChance}
                    getChancePercentage={getChancePercentage}
                    setSelectedAddValueAttribute={setSelectedAddValueAttribute}
                    setSelectedEditValue={setSelectedEditValue}
                    setSelectedBlockAttributesAttribute={
                        setSelectedBlockAttributesAttribute
                    }
                    setSelectedDeleteAttribute={setSelectedDeleteAttribute}
                    setAddAttributeDialogOpened={setAddAttributeDialogOpened}
                    updateLayers={updateLayers}
                    setSelectedDisplayAllImagesAttribute={
                        setSelectedDisplayAllImagesAttribute
                    }
                />
            </div>

            <div className="flex justify-end">
                <div className="my-auto">
                    {attributes.length === 0 ? (
                        <>
                            <MainButton
                                className="w-28 mx-2 my-auto"
                                fullWidth
                                startContent={<CloudDownloadOutlinedIcon />}
                                size="small"
                                isDisabled
                            >
                                Save
                            </MainButton>
                        </>
                    ) : (
                        <a
                            href={
                                'data:text/json;charset=utf-8,' +
                                encodeURIComponent(
                                    JSON.stringify({
                                        attributes,
                                        blockRules,
                                    }),
                                )
                            }
                            download="attributes.json"
                        >
                            <MainButton
                                className="w-28 mx-2 my-auto"
                                fullWidth
                                startContent={<CloudDownloadOutlinedIcon />}
                                size="small"
                            >
                                Save
                            </MainButton>
                        </a>
                    )}
                </div>
                <div className="my-auto">
                    <input
                        accept=".json"
                        style={{ display: 'none' }}
                        id="json-upload"
                        onChange={handleSavedStateUpload}
                        type="file"
                    />
                    <label htmlFor="json-upload">
                        <MainButton
                            className="w-28 mx-2 my-auto"
                            component="span"
                            fullWidth
                            startIcon={<CloudUploadOutlinedIcon />}
                            as="span"
                            size="small"
                        >
                            Load
                        </MainButton>
                    </label>
                </div>
                <Tooltip
                    title={
                        <div className="text-base">
                            You can save and load your progress here. Clicking
                            "SAVE" will download a{' '}
                            <span className="font-mono">.json</span> file to
                            your device, which you can upload at any point to
                            restore your progress using the "LOAD" button. This
                            will <b>not</b> read from or write to the
                            blockchain.
                        </div>
                    }
                >
                    <div className="my-auto">
                        <IconButton size="small" color="primary">
                            <HelpOutlineOutlinedIcon />
                        </IconButton>
                    </div>
                </Tooltip>
            </div>
            <DropAlert condition={hasDuplicateLayers()} severity="error">
                Duplicate Layers detected!
            </DropAlert>
            <DropAlert condition={parseError !== null} severity="error">
                {parseError}
            </DropAlert>
            <DropAlert
                condition={hasInconsistentResolutions()}
                severity="error"
            >
                {hasInconsistentResolutions()}
            </DropAlert>
        </DropPaper>
    )
}
